import { VendorCustomTemplateType } from "../../Vendor/EmailTemplate";
import { SystemEmailTemplateContent } from "../../EmailTemplates";

export enum EmailThreadMessageEditorType {
	Compose = "email-thread-message-compose-editor",
	Reply = "email-thread-message-reply-editor",
	Automate = "email-thread-message-automation-editor"
}

export class EmailThreadLabel {
	display_name = "";
	id = "";
	name = "";
}

export enum EntityType {
	Document = "document_type",
	Opportunity = "opportunity_type",
	Client = "client_type",
	Partner = "partner_type",
	VendorOpportunity = "vendor_opportunity_type",
	VendorClient = "vendor_client_type",
	VendorTemplate = "vendor_template_type",
	User = "user_type",
	PurchaseOrder = "purchase_order_type"
}

export interface QueryParameters {
	entity_type: string;
	entity_id: string;
}

export class FetchMergeTagsRequest {
	documentId?: string | null;
	opportunityId?: string | null;
	clientId?: string | null;
	partnerId?: string | null;
	purchaseOrderId?: string | null;

	constructor(args: {
		documentId?: string | null;
		opportunityId?: string | null;
		clientId?: string | null;
		partnerId?: string | null;
		purchaseOrderId?: string | null;
	}) {
		this.opportunityId = args.opportunityId;
		this.documentId = args.documentId;
		this.clientId = args.clientId;
		this.partnerId = args.partnerId;
		this.purchaseOrderId = args.purchaseOrderId;
	}

	toEntityQueryString = () => {
		const result: QueryParameters[] = [];

		if (this.opportunityId) {
			result.push({
				entity_type: EntityType.Opportunity,
				entity_id: this.opportunityId
			});
		}

		if (this.documentId) {
			result.push({
				entity_type: EntityType.Document,
				entity_id: this.documentId
			});
		}

		if (this.clientId) {
			result.push({
				entity_type: EntityType.Client,
				entity_id: this.clientId
			});
		}

		if (this.purchaseOrderId) {
			result.push({
				entity_type: EntityType.PurchaseOrder,
				entity_id: this.purchaseOrderId
			});
		}

		return result;
	};
	toEntityQueryStringforVendor = () => {
		const result: QueryParameters[] = [];

		if (this.partnerId && this.opportunityId) {
			result.push({
				entity_type: EntityType.VendorOpportunity,
				entity_id: this.opportunityId
			});
		} else if (this.opportunityId) {
			result.push({
				entity_type: EntityType.VendorOpportunity,
				entity_id: this.opportunityId
			});
		} else if (this.partnerId) {
			result.push({
				entity_type: EntityType.Partner,
				entity_id: this.partnerId
			});
		} else if (this.clientId) {
			result.push({
				entity_type: EntityType.VendorClient,
				entity_id: this.clientId
			});
		}
		return result;
	};
}

export class EmailThreadParticipant {
	email: string | null = "";
	name = "";
}

export class EmailThreadRecipients {
	email: string | null = null;
	name = "";
	id = "";
	is_nylas?: boolean;
}

export class EmailThreadFile {
	account_id = "";
	content_disposition = "";
	content_type = "";
	filename = "";
	id = "";
	size = 0;
	file_size? = 0;
	file_name? = "";
	file_id?: string;
	message?: string;
}

export class EmailThreadMessage {
	account_id = "";
	bcc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	body = "";
	body_updated: Date = new Date();
	cc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	date_time: Date = new Date();
	draft_ids: string[] = new Array<string>();
	email_id = "";
	email_ids_to_fetch: string[] = [];
	files: EmailThreadFile[] = new Array<EmailThreadFile>();
	file_ids: string[] = new Array<string>();
	from: EmailThreadParticipant = new EmailThreadParticipant();
	has_attachments = false;
	id = "";
	labels: EmailThreadLabel[] = new Array<EmailThreadLabel>();
	last_message_time_stamp: Date = new Date();
	message_ids: string[] = new Array<string>();
	object = "";
	participants: EmailThreadParticipant[] =
		new Array<EmailThreadParticipant>();
	pinned = false;
	reply_to: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	snippet = "";
	starred = false;
	subject = "";
	to: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	unread = false;
	version = 0;
}

export class EmailThreadMessagesResponse {
	data: EmailThreadMessage[] = new Array<EmailThreadMessage>();
}

export class DownloadEmailThreadMessageFileRequest {
	file: EmailThreadFile;
	account_id?: string;
	message_id?: string;
	constructor(
		file: EmailThreadFile,
		account_id?: string,
		message_id?: string
	) {
		this.file = file;
		this.account_id = account_id;
		this.message_id = message_id;
	}
}

export class SendEmailThreadMessageFormValues {
	account_id = "";
	body = "";
	subject = "";
	to: string[] = new Array<string>();
	cc: string[] = new Array<string>();
	bcc: string[] = new Array<string>();
	file_ids: string[] = new Array<string>();
	file_names?: string[] = new Array<string>();
	from = "";
	tracking: EmailTracking | null = null;
	should_replace_with_empty_string = true;
	s3_file_ids?: EmailAttachmentFileS3[] = new Array<EmailAttachmentFileS3>();
	use_default_email? = true;
	attach_document? = false;
	attach_signing_log? = false;
	attach_payment_log? = false;
	attach_product_list? = false;
}

export class SendEmailThreadMessageRequest {
	account_id = "";
	body = "";
	signature = "";
	subject = "";
	to: EmailThreadRecipients[] = new Array<EmailThreadRecipients>();
	cc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	bcc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	file_ids: string[] = new Array<string>();
	document_id = "";
	from: EmailThreadRecipients | null = new EmailThreadRecipients();
	should_replace_with_empty_string = true;
	mail_id = "";
	mail_type: EntityType = EntityType.Client;
	tracking: EmailTracking | null = null;
	reply_to_message_id: string | undefined = "";
}

export class EmailTracking {
	links = false;
	opens = false;
	thread_replies = false;
}

export enum EmailRecipientsTypes {
	TO = "to",
	CC = "cc",
	BCC = "bcc"
}

export class SendDocumentEmailThreadMessageRequest {
	from: EmailThreadRecipients | null = new EmailThreadRecipients();
	body = "";
	signature = "";
	subject = "";
	to?: EmailThreadRecipients[] = new Array<EmailThreadRecipients>();
	cc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	bcc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	document_id = "";
	should_replace_with_empty_string = true;
	file_ids: string[] = new Array<string>();
}

export class ValidateEmailMergeTagsRequest {
	from: EmailThreadRecipients | null = new EmailThreadRecipients();
	to: EmailThreadRecipients[] = new Array<EmailThreadRecipients>();
	cc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	bcc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	body = "";
	subject = "";
	mail_id = "";
	mail_type: EntityType | null = null;
}
export class GetEmailPreviewRequest {
	from: EmailThreadRecipients | null = new EmailThreadRecipients();
	to: EmailThreadRecipients[] = new Array<EmailThreadRecipients>();
	cc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	bcc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	body = "";
	subject = "";
	mail_id = "";
	mail_type: EntityType = EntityType.Client;
	should_replace_with_empty_string = true;
	account_id = "";
	file_ids: string[] = new Array<string>();
	system_template_info?: SystemEmailTemplateContent | null = null;
	signature = "";
	template_type?: VendorCustomTemplateType =
		VendorCustomTemplateType.PublishTemplate;
}

export class GetDocumentEmailPreviewRequest {
	to?: EmailThreadRecipients[] = new Array<EmailThreadRecipients>();
	cc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	bcc: EmailThreadParticipant[] = new Array<EmailThreadParticipant>();
	body = "";
	subject = "";
	should_replace_with_empty_string = true;
	documentId = "";
	signature = "";
}

export interface OtherEntityEmailPreview {
	subject: string;
	body: string;
	system_template_info_view?: SystemEmailTemplateContent | null;
}

export interface EmptyMergeTags {
	present_and_empty_tags: string[];
}

export interface ValidateEmailMergeTagsResponse {
	present_and_empty_tags: string[];
	to: {
		name: string;
		email: string;
	};
}

export interface EmailPreviewResponse {
	from: EmailThreadParticipant[] | null;
	to: EmailThreadParticipant[];
	subject: "" | null;
	body: "";
	system_template_info_view?: SystemEmailTemplateContent | null;
}

export interface LastUsedEmail {
	email: string | null;
	name: string | null;
}

export interface sendMs365InviteToClientRequest {
	clientId: string;
	ms365Invite: Partial<SendEmailThreadMessageRequest>;
}

export interface EmailAttachmentFileS3 {
	file_name: string;
	url: string;
	file_id?: string;
}
